import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import { icons } from "../../../icons";
import { HeadLine3 } from "../../typography/styled";
import Button from "../../button";
import Input from "../../input";
import Select from "../../select";
import { clusterSettingsService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import {
  Container,
  HeadWrapper,
  BodyModal,
  Icon,
} from "../create-node-pool/styles";

type ModalProps = {
  closeModalCancel: () => void;
  closeModal: (data?: any) => void;
  dataToEdit?: any;
};

const WORKER_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "busy", label: "Busy" },
  { value: "pending", label: "Pending" },
  { value: "installing", label: "Installing" },
  { value: "deleting", label: "Deleting" },
  { value: "error", label: "Error" },
];

const CreateWorker: FC<ModalProps> = ({ closeModal, closeModalCancel, dataToEdit }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [loading, setLoading] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const isEdit = !!dataToEdit?.id;

  const onSubmit = (values: any) => {
    setLoading(true);
    const action = isEdit
      ? clusterSettingsService.updateWorker(dataToEdit.id, {
          status: values.status,
          config_file: values.config_file || undefined,
        })
      : clusterSettingsService.createWorker({
          name: values.name,
          type: values.type,
        });

    action
      .then(() => {
        setLoadingComplete(true);
        setTimeout(() => {
          closeModal({ success: true });
          setLoading(false);
          setLoadingComplete(false);
        }, 1500);
      })
      .catch((err) => {
        setLoading(false);
        viewAlert({
          severity: "error",
          message: err?.response?.data?.detail || t("ERROR_OCCURRED"),
        });
      });
  };

  const { values, errors, touched, handleSubmit, handleChange, setFieldValue } = useFormik({
    enableReinitialize: true,
    validationSchema: isEdit
      ? yup.object().shape({
          status: yup.string().required(t("REQUIRED_FIELD")),
        })
      : yup.object().shape({
          name: yup.string().required(t("REQUIRED_FIELD")),
          type: yup.string().required(t("REQUIRED_FIELD")),
        }),
    initialValues: isEdit
      ? {
          status: dataToEdit?.status || "active",
          config_file: dataToEdit?.config_file || "",
          name: "",
          type: "",
        }
      : {
          name: "",
          type: "",
          status: "active",
          config_file: "",
        },
    onSubmit,
  });

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: "10px", marginTop: "10px" }}>
          {isEdit ? t("EDIT_WORKER") : t("CREATE_WORKER")}
        </HeadLine3>
        <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        {isEdit ? (
          <>
            <Select title={t("STATUS")} data={WORKER_STATUS_OPTIONS} selectedValue={values.status} onChange={(val: any) => setFieldValue("status", val)} fieldName="status" errors={errors} touched={touched} required />
            <Input title={t("CONFIG_FILE")} fieldName="config_file" value={values.config_file} handleChange={handleChange} errors={errors} touched={touched} isTextArea />
          </>
        ) : (
          <>
            <Input title={t("NAME")} fieldName="name" value={values.name} handleChange={handleChange} errors={errors} touched={touched} required />
            <Input title={t("TYPE")} fieldName="type" value={values.type} handleChange={handleChange} errors={errors} touched={touched} required />
          </>
        )}
        <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
          <Button variant="stroke" size="display" customStyles={{ width: "177px", justifyContent: "center" }} onClick={closeModalCancel} title={t("CANCEL")} />
          <Button variant="primary" size="display" loading={loading} loadingComplete={loadingComplete} customStyles={{ width: "177px", justifyContent: "center" }} onClick={handleSubmit} title={isEdit ? t("SAVE") : t("CREATE")} />
        </div>
      </BodyModal>
    </Container>
  );
};

export default CreateWorker;
